import type { Coordinate } from "../../entities/tracking";

/**
 * Distance à vol d'oiseau entre deux points, en kilomètres (formule de haversine).
 *
 * Le backend ne fournit pas de service de routing : c'est la seule distance dont
 * l'app dispose. Elle sert à deux choses :
 *  - dériver une distance/ETA réelle depuis la position GPS live de l'agent
 *    (suivi de commande) ;
 *  - alimenter `distance_km` à la création d'une course, pour que le backend
 *    calcule un vrai tarif au lieu d'une distance forfaitaire.
 *
 * ⚠️ C'est une distance à vol d'oiseau, pas une distance routière : elle
 * sous-estime toujours le trajet réel. Le backend l'utilise comme base de
 * tarification, ce qui est assumé — pas comme une promesse de trajet.
 */
export function haversineKm(a: Coordinate, b: Coordinate): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
